import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, ApolloError } from '@apollo/client';
import SanitizedHtmlBox from './SanitizedHtmlBox';
import resendSystemAction from '../api/resendSystemAction';
import type { ResendMailSnapshot, SystemAccessLogEntry, SystemAccessLogHistoryEntry } from '../types';
import {
  formatDateTime,
  formatScheduledDateBerlin,
  sortHistory,
  resolveSubject,
  resolveDisplaySubject,
  resolveScheduledAt,
  computeHistoryBadges,
  getLastFailedAttempt,
  describePlannedSend,
  canCancelEntry,
  canEditEntry,
  canResendEntry,
  formatErrorReason,
  isTechnicalError,
} from '../utils/format';

const RESEND_COLOR = '#58A6FF';

// Stable, generic user-facing message for a failed resend. Used both as the fallback
// when the server sends no safe message and for raw network/unknown errors, so internal
// infrastructure details never reach the editor (Audit P2). Mirrors the backend's fixed
// EMAIL_SEND_FAILED text.
const RESEND_ERROR_FALLBACK = 'Die E-Mail konnte nicht erneut versendet werden. Bitte später erneut versuchen.';

const HISTORY_ENTRY_LABEL: Record<SystemAccessLogHistoryEntry['entry_type'], string> = {
  created: 'Geplant erstellt',
  updated: 'Inhalt/Termin geändert',
  sent: 'E-Mail versendet',
  resent: 'Erneut versendet',
  cancelled: 'Versand abgebrochen',
  failed: 'Versand fehlgeschlagen',
};

interface GridField {
  k: string;
  v: string;
}

function getDetailGrid(entry: SystemAccessLogEntry, sorted: SystemAccessLogHistoryEntry[]): GridField[] {
  const sends = sorted.filter((h) => h.entry_type === 'sent' || h.entry_type === 'resent');
  const fails = sorted.filter((h) => h.entry_type === 'failed');
  const lastChange = [...sorted].reverse().find((h) => h.entry_type === 'updated' || h.entry_type === 'created');
  // The log row keeps the creator as triggered_by; the actor who cancelled /
  // the time of a failed attempt only live in the append-only history.
  const cancelEntry = [...sorted].reverse().find((h) => h.entry_type === 'cancelled');
  const lastFail = getLastFailedAttempt(sorted);

  switch (entry.process_status) {
    case 'planned': {
      // A planned row with no scheduled_at is a claimed-but-unsent immediate
      // send — label it "Sofort", not a phantom planned date.
      const plannedSend = describePlannedSend(entry.scheduled_at);
      return [
        { k: 'Erstellt am', v: formatDateTime(entry.createdAt) },
        { k: 'Zuletzt geändert', v: lastChange ? formatDateTime(lastChange.createdAt) : '—' },
        { k: plannedSend.gridKey, v: plannedSend.gridValue },
        { k: 'Versendet wird', v: 'aktuelle Fassung' },
      ];
    }
    case 'applied':
      return [
        {
          k: 'Erstmals versendet',
          v: sends[0] ? formatDateTime(sends[0].createdAt) : formatDateTime(entry.executed_at),
        },
        {
          k: 'Zuletzt versendet',
          v: sends.length ? formatDateTime(sends[sends.length - 1].createdAt) : formatDateTime(entry.executed_at),
        },
        { k: 'Zeitpunkt', v: entry.scheduled_at ? 'Terminiert' : 'Sofort' },
        { k: 'Anzahl Versände', v: String(Math.max(sends.length, 1)) },
      ];
    case 'not_sent':
      return [
        // Immediate failures leave scheduled_at null — don't claim a planned
        // send that never existed; show the timing the same way `applied` does.
        entry.scheduled_at
          ? { k: 'Geplant war', v: formatScheduledDateBerlin(entry.scheduled_at) }
          : { k: 'Zeitpunkt', v: 'Sofort' },
        { k: 'Status', v: 'Fehlgeschlagen' },
        // executed_at is not stamped on failure — take the attempt time from
        // the latest `failed` history entry instead.
        { k: 'Letzter Versuch', v: lastFail ? formatDateTime(lastFail.createdAt) : formatDateTime(entry.executed_at) },
        { k: 'Versuche', v: String(Math.max(fails.length, 1)) },
      ];
    case 'cancelled':
      return [
        { k: 'War geplant', v: formatScheduledDateBerlin(entry.scheduled_at) },
        { k: 'Abgebrochen am', v: formatDateTime(entry.cancelled_at) },
        // The canceller lives ONLY in the cancel history entry — the log row's
        // triggered_by still points at the creator. Never fall back to the
        // creator here; if the canceller can't be resolved, degrade to '—'.
        { k: 'Abgebrochen von', v: cancelEntry ? cancelEntry.triggered_by_name || '—' : '—' },
        { k: 'Grund', v: entry.cancel_reason || '—' },
      ];
    default:
      return [];
  }
}

function getMailHeading(entry: SystemAccessLogEntry): { title: string; subtitle: string } {
  switch (entry.process_status) {
    case 'planned':
      // scheduled_at=null on a planned row is a claimed-but-unsent immediate
      // send (in flight / stale-claim recovery), not a scheduled one — say
      // "sofort", never "terminiert".
      return {
        title: 'Aktuelle E-Mail',
        subtitle: describePlannedSend(entry.scheduled_at).headingSubtitle,
      };
    case 'applied':
      return { title: 'Versendete E-Mail', subtitle: 'versendet' };
    case 'not_sent':
      return { title: 'Nicht versendete E-Mail', subtitle: 'nicht versendet' };
    case 'cancelled':
      return { title: 'E-Mail', subtitle: 'Versand abgebrochen' };
    default:
      return { title: 'E-Mail', subtitle: '' };
  }
}

const UPPER_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: 'uppercase' as const,
};

interface Props {
  entry: SystemAccessLogEntry;
  onCancel: (entry: SystemAccessLogEntry) => void;
  // Opens the edit dialog for a still-scheduled row (RITA-1317). Owned by the panel so
  // the dialog state survives this detail unmounting under Collapse `unmountOnExit`,
  // mirroring onCancel.
  onEdit: (entry: SystemAccessLogEntry) => void;
  // True while this entry's saved edit is awaiting its post-edit log refresh. Panel-owned and
  // keyed by id (survives this detail unmounting), it locks the row's edit/cancel affordances so
  // the operator can't re-open the dialog on the still-stale, pre-refresh content (Audit/Review P2).
  editPending: boolean;
  // True while this entry has a confirmed resend whose durable log refresh has not
  // landed yet. Owned by the panel and keyed by entry id, so it survives this detail
  // unmounting (Collapse `unmountOnExit`, paging, filtering) and is dropped ONLY after
  // refetchLog() actually succeeds (Codex Review P1).
  resendLatched: boolean;
  // Marks this entry as resent in the panel and resolves once the parent's log refetch
  // settles; rejects if that refetch fails so the detail can keep the button replaced. The
  // advisorId is this row's own advisor_id so the panel files the latch under the ORIGINATING
  // advisor even across a client-side advisor switch (Codex Review P1).
  onResendSuccess: (entryId: number, advisorId: number) => Promise<unknown>;
  // Non-null while this entry's resend mail went out but its durable record rolled back
  // (backend committed=false). Carries the resend-time mail header snapshot. Panel-owned and
  // TERMINAL — never released by a refetch — so the button stays replaced by a "reload" warning
  // (rendering the snapshot, NOT the live row that a later refetch can re-merge/null out) and a
  // duplicate send can't be triggered on the unchanged row (Codex Review P1).
  resendUnrecordedSnapshot: ResendMailSnapshot | null;
  // Marks this entry as delivered-but-unrecorded in the panel (commit-phase failure), carrying
  // the resend-time mail header snapshot.
  onResendUnrecorded: (entryId: number, snapshot: ResendMailSnapshot) => void;
}

function SystemAccessLogDetail({
  entry,
  onCancel,
  onEdit,
  editPending,
  resendLatched,
  onResendSuccess,
  resendUnrecordedSnapshot,
  onResendUnrecorded,
}: Props) {
  const theme = useTheme();
  const [openDiffIdx, setOpenDiffIdx] = useState<number | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  // Success latch: once a resend succeeds the button is replaced by a confirmation so
  // it can NOT be clicked again. The mail already went out; the panel then refetches
  // the log. The DURABLE latch is `resendLatched` (panel-owned): it survives this
  // detail unmounting and is only released after the refetch truly succeeds, closing
  // the duplicate-resend window the reviewer flagged (Codex Review P1) — a transient
  // refetch failure used to leave the row stale while the button stayed clickable.
  // `justResent` only mirrors that latch within the current mount so the confirmation
  // shows instantly (before the panel state propagates) and no click can slip through
  // the gap; it is cleared the moment the panel drops the latch, re-enabling a
  // legitimate sequential resend.
  const [justResent, setJustResent] = useState(false);
  useEffect(() => {
    if (!resendLatched) setJustResent(false);
  }, [resendLatched]);
  const resendConfirmed = resendLatched || justResent;
  // Instant in-mount mirror of the panel's terminal "delivered but not recorded" latch
  // (commit-phase failure) — and the resend-time mail header snapshot that goes with it. Set the
  // moment the mutation returns committed=false so the button is replaced by the warning, showing
  // the values that actually went out, BEFORE the panel state propagates — no click can slip
  // through the gap. The panel snapshot (`resendUnrecordedSnapshot`) keeps it across unmounts;
  // this local mirror is never cleared (the state is terminal until a reload) (Codex Review P1).
  const [localUnrecordedSnapshot, setLocalUnrecordedSnapshot] = useState<ResendMailSnapshot | null>(null);
  const activeUnrecordedSnapshot = resendUnrecordedSnapshot ?? localUnrecordedSnapshot;
  const resendDeliveredUnrecorded = activeUnrecordedSnapshot !== null;
  const [resendMutation, { loading: resending }] = useMutation(resendSystemAction);

  const sorted = sortHistory(entry.history);
  const badges = computeHistoryBadges(sorted);
  // Planned rows prefer the backend's live `current_subject`; sent/failed/cancelled
  // rows use the subject that actually went out (newest history snapshot), falling
  // back to the frozen log-row snapshot carried on `current_subject` when a swallowed
  // post-send audit append left the history without a subject (RITA-1307 Review P1).
  // The transient-window override keys off the DURABLE, panel-owned `resendLatched`,
  // NOT `resendConfirmed`: the latch is set the moment the resend confirms and dropped
  // ONLY after the authoritative log refetch lands, so it marks exactly the window in
  // which the cached history is still PRE-resend and the freshly-merged `current_subject`
  // should win. `resendConfirmed` additionally carries the in-mount `justResent` flag,
  // which is cleared only in a post-paint effect — so it stays true for one render AFTER
  // the refetch already landed and the committed `resent` history is available. Keying
  // the subject off `resendConfirmed` would, on that render, re-prefer the (possibly
  // stale best-effort) `current_subject` over the authoritative history and flash the
  // wrong subject at the customer on every successful resend. Keying off the latch
  // removes that flash; `justResent` stays scoped to the button lock below (RITA-1316
  // Review C/P1). See resolveDisplaySubject for the full priority.
  const currentSubject = resolveDisplaySubject(entry, resendLatched);
  // In the terminal delivered-but-unrecorded state the live row can no longer be trusted for
  // the mail header: there is no refetch, and a later cache-and-network GetAdvisorSystemLog
  // re-merges the unchanged, rolled-back row (which for an already-sent row carries no
  // recipient/sender and a stale subject) over the mutation-merged scalars. Render the captured
  // resend-time snapshot instead, so An:/Von:/Betreff keep showing what actually went out
  // (Codex Review P1). Outside that state nothing changes.
  const displayRecipient = activeUnrecordedSnapshot ? activeUnrecordedSnapshot.recipient_email : entry.recipient_email;
  const displayMailFrom = activeUnrecordedSnapshot ? activeUnrecordedSnapshot.mail_from : entry.mail_from;
  const displaySubject = activeUnrecordedSnapshot ? activeUnrecordedSnapshot.subject : currentSubject;
  const heading = getMailHeading(entry);
  const grid = getDetailGrid(entry, sorted);
  const canCancel = canCancelEntry(entry);
  const canEdit = canEditEntry(entry);
  const canResend = canResendEntry(entry);

  const handleResend = async () => {
    setResendError(null);
    try {
      const { data } = await resendMutation({ variables: { id: entry.id } });
      if (data?.resendSystemAction?.success) {
        if (data.resendSystemAction.committed === false) {
          // The mail went out but its durable record rolled back in the COMMIT phase. The
          // row is unchanged, so the NORMAL success flow (refetch → latch release →
          // canResendEntry) would re-enable the button on a row that merely looks
          // resendable and invite a DUPLICATE send. Lock terminally instead and warn the
          // editor to reload — do NOT refetch (Codex Review P1).
          //
          // Capture the resend-time mail header from the mutation payload (the backend
          // attaches the recipient/sender/subject this resend used even on the rolled-back
          // path). Render THIS snapshot in the warning, not the live row: with no refetch the
          // merged scalars would otherwise be re-merged/nulled by a later cache-and-network
          // GetAdvisorSystemLog over the unchanged row, showing the pre-resend mailbox for a
          // mail that already went out (Codex Review P1).
          const sentSnapshot = data.resendSystemAction.log_entry;
          const snapshot: ResendMailSnapshot = {
            recipient_email: sentSnapshot?.recipient_email ?? null,
            mail_from: sentSnapshot?.mail_from ?? null,
            subject: sentSnapshot?.current_subject ?? null,
          };
          setLocalUnrecordedSnapshot(snapshot);
          onResendUnrecorded(entry.id, snapshot);
          return;
        }
        // Lock instantly: the mail is already out, so the button must be replaced even
        // while the panel state propagates. The panel sets the durable latch and only
        // releases it after the log refetch lands.
        setJustResent(true);
        try {
          await onResendSuccess(entry.id, entry.advisor_id);
        } catch {
          // Refetch failed — the panel keeps the latch up, so the button stays replaced
          // and the editor can't fire a duplicate resend. Reloading the view recovers
          // the fresh state.
        }
      } else {
        setResendError(data?.resendSystemAction?.message || RESEND_ERROR_FALLBACK);
      }
    } catch (err) {
      // Surface the deliberate, safe backend message (e.g. RESEND_IN_PROGRESS or the
      // generic EMAIL_SEND_FAILED text) when it's a GraphQL error, but never echo a raw
      // network/technical string — fall back to the fixed generic message for non-GraphQL
      // errors so no internal detail leaks to the user (Audit P2).
      const gqlMessage = err instanceof ApolloError ? err.graphQLErrors[0]?.message : undefined;
      setResendError(gqlMessage || RESEND_ERROR_FALLBACK);
    }
  };

  return (
    <Box
      data-testid={`log-row-details-${entry.id}`}
      sx={{ px: 2, py: 2, bgcolor: alpha(theme.palette.text.primary, 0.02) }}
    >
      {/* Metadata grid */}
      {grid.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5,
            mb: 2,
          }}
        >
          {grid.map((field) => (
            <Box key={field.k}>
              <Typography sx={{ ...UPPER_LABEL_SX, fontSize: 10, color: theme.palette.text.disabled, mb: 0.25 }}>
                {field.k}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{field.v}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Error reason badge + detail — only for failed entries */}
      {/*
       * CONTRACT: error-reason badge section renders ONLY when BOTH are true:
       * - entry.status === 'failed' (applied/planned/cancelled never show a badge, even if error_reason is populated in DB)
       * - entry.error_reason is non-null (not_sent rows without a classified reason show nothing)
       */}
      {entry.status === 'failed' && entry.error_reason && (
        <Box sx={{ mb: 2 }}>
          <Box
            data-testid={`error-reason-badge-${entry.id}`}
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 0.85,
              py: 0.25,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: '#EF4444',
              bgcolor: alpha('#EF4444', 0.12),
              border: `1px solid ${alpha('#EF4444', 0.3)}`,
              whiteSpace: 'nowrap',
            }}
          >
            {formatErrorReason(entry.error_reason)}
          </Box>
          {/*
           * CONTRACT: error-detail box renders ONLY when BOTH are true:
           * - isTechnicalError(entry.error_reason) is true (only 'technisch' qualifies)
           * - entry.error_detail is non-null (technical errors without detail show badge only, no monospace box)
           */}
          {isTechnicalError(entry.error_reason) && entry.error_detail && (
            <Box
              data-testid={`error-detail-${entry.id}`}
              sx={{
                mt: 0.75,
                p: 0.75,
                borderRadius: 1,
                bgcolor: alpha('#EF4444', 0.07),
                border: `1px solid ${alpha('#EF4444', 0.25)}`,
                userSelect: 'text',
              }}
            >
              <Typography
                sx={{ fontSize: 11, fontFamily: 'monospace', color: '#EF4444', lineHeight: 1.4, userSelect: 'text' }}
              >
                {entry.error_detail}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Current mail block */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ ...UPPER_LABEL_SX, color: theme.palette.text.disabled }}>{heading.title}</Typography>
        {heading.subtitle && (
          <Typography
            data-testid={`current-mail-subtitle-${entry.id}`}
            sx={{ fontSize: 12, color: theme.palette.text.secondary, fontStyle: 'italic' }}
          >
            {heading.subtitle}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: alpha(theme.palette.background.default, 0.6),
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          mb: 2,
        }}
      >
        <Box sx={{ mb: 1, pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          {/* Recipient is only shown for planned rows. For already-sent rows the
              backend omits it, because the live provider address may differ
              from the one the mail actually went to (no recipient snapshot yet). */}
          {displayRecipient && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
              <strong>An:</strong> {displayRecipient}
            </Typography>
          )}
          {/* Sender is only shown for planned rows. For already-sent rows the
              backend omits it, because the live MAIL_FROM may differ from the
              address the mail actually went out with (no sender snapshot yet). */}
          {displayMailFrom && (
            <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
              <strong>Von:</strong> {displayMailFrom}
            </Typography>
          )}
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
            <strong>Betreff:</strong> {displaySubject || '—'}
          </Typography>
        </Box>
        <SanitizedHtmlBox
          html={entry.mail_content}
          sx={{
            fontSize: 13,
            lineHeight: 1.6,
            color: theme.palette.text.primary,
            maxHeight: 220,
            overflowY: 'auto',
            '& p': { m: 0, mb: 1 },
          }}
        />
      </Box>

      {/* Explainer for cancelled rows. The actual reason is shown in the
          metadata grid above ("Grund"); this box only clarifies that no mail
          was sent (mirrors the mockup). */}
      {entry.process_status === 'cancelled' && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: alpha('#9CA3AF', 0.08),
            border: `1px solid ${alpha('#9CA3AF', 0.3)}`,
          }}
        >
          <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary }}>
            Dieser geplante Versand wurde vor Ausführung manuell abgebrochen — es ging keine E-Mail raus. Kein Fehler,
            sondern bewusst gestoppt.
          </Typography>
        </Box>
      )}

      {/* History with inline diff */}
      <Typography sx={{ ...UPPER_LABEL_SX, color: theme.palette.text.disabled, mb: 1 }}>Verlauf</Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {sorted.map((h, idx) => {
          const isCurrent = idx === sorted.length - 1;
          const isDiffOpen = openDiffIdx === idx;
          const before = idx > 0 ? sorted[idx - 1] : null;
          const afterSubject = resolveSubject(h, sorted, idx);
          const beforeSubject = resolveSubject(before, sorted, idx - 1);
          // Carry the planned date forward: terminal sent/failed rows have
          // scheduled_at=null, but the plan never disappeared. Showing the
          // last known schedule keeps the audit trail honest and makes a
          // schedule-only change auditable instead of looking like a no-op.
          const afterSchedule = resolveScheduledAt(h, sorted, idx);
          const beforeSchedule = resolveScheduledAt(before, sorted, idx - 1);
          const showSchedule = Boolean(beforeSchedule || afterSchedule);

          return (
            <Box
              key={h.id}
              component="li"
              data-testid={`log-row-history-item-${entry.id}-${idx}`}
              sx={{
                display: 'block',
                borderRadius: 1,
                border: `1px solid ${isCurrent ? alpha('#22C55E', 0.35) : theme.palette.divider}`,
                bgcolor: isCurrent ? alpha('#22C55E', 0.06) : 'transparent',
                overflow: 'hidden',
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={isDiffOpen}
                data-testid={`log-row-history-toggle-${entry.id}-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDiffIdx((cur) => (cur === idx ? null : idx));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpenDiffIdx((cur) => (cur === idx ? null : idx));
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  cursor: 'pointer',
                  fontSize: 12,
                  '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) },
                  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
                }}
              >
                <Box
                  data-testid={`log-row-history-badge-${entry.id}-${idx}`}
                  sx={{
                    flex: '0 0 auto',
                    px: 0.85,
                    py: 0.2,
                    borderRadius: 0.75,
                    fontSize: 11,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    color: isCurrent ? '#16A34A' : theme.palette.text.secondary,
                    bgcolor: isCurrent ? alpha('#22C55E', 0.14) : alpha(theme.palette.text.primary, 0.06),
                  }}
                >
                  {badges[idx]}
                  {isCurrent ? ' · aktuell' : ''}
                </Box>
                <Box sx={{ minWidth: 130, color: theme.palette.text.secondary }}>{formatDateTime(h.createdAt)}</Box>
                <Box sx={{ fontWeight: 700, flex: '0 0 auto' }}>
                  {HISTORY_ENTRY_LABEL[h.entry_type] || h.entry_type}
                </Box>
                <Box
                  sx={{
                    color: theme.palette.text.disabled,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h.triggered_by_name ? `· ${h.triggered_by_name}` : ''}
                </Box>
                <Box
                  sx={{
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Inhalt {before ? 'vorher/nachher' : 'ansehen'}
                  <ExpandMoreIcon
                    sx={{
                      fontSize: 16,
                      transition: 'transform .15s',
                      transform: isDiffOpen ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </Box>
              </Box>

              {isDiffOpen && (
                <Box data-testid={`log-row-history-diff-${entry.id}-${idx}`} sx={{ px: 1.25, pb: 1.25, pt: 0.5 }}>
                  {before ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 1,
                      }}
                    >
                      <Box
                        data-testid={`diff-before-${entry.id}-${idx}`}
                        sx={{
                          borderRadius: 1,
                          border: `1px solid ${alpha('#EF4444', 0.3)}`,
                          bgcolor: alpha('#EF4444', 0.06),
                          p: 1,
                        }}
                      >
                        <Typography sx={{ ...UPPER_LABEL_SX, fontSize: 10, color: '#EF4444', mb: 0.5 }}>
                          Vorher
                        </Typography>
                        <Typography sx={{ fontSize: 12, mb: 0.5, color: theme.palette.text.secondary }}>
                          <strong>Betreff:</strong> {beforeSubject || '—'}
                        </Typography>
                        {showSchedule && (
                          <Typography
                            data-testid={`schedule-before-${entry.id}-${idx}`}
                            sx={{ fontSize: 12, mb: 0.5, color: theme.palette.text.secondary }}
                          >
                            <strong>Geplant:</strong> {beforeSchedule ? formatScheduledDateBerlin(beforeSchedule) : '—'}
                          </Typography>
                        )}
                        <SanitizedHtmlBox
                          html={before.mail_content_snapshot}
                          sx={{ fontSize: 12.5, lineHeight: 1.55, '& p': { m: 0, mb: 0.5 } }}
                        />
                      </Box>
                      <Box
                        data-testid={`diff-after-${entry.id}-${idx}`}
                        sx={{
                          borderRadius: 1,
                          border: `1px solid ${alpha('#22C55E', 0.3)}`,
                          bgcolor: alpha('#22C55E', 0.06),
                          p: 1,
                        }}
                      >
                        <Typography sx={{ ...UPPER_LABEL_SX, fontSize: 10, color: '#16A34A', mb: 0.5 }}>
                          Nachher
                        </Typography>
                        <Typography sx={{ fontSize: 12, mb: 0.5, color: theme.palette.text.secondary }}>
                          <strong>Betreff:</strong> {afterSubject || '—'}
                        </Typography>
                        {showSchedule && (
                          <Typography
                            data-testid={`schedule-after-${entry.id}-${idx}`}
                            sx={{ fontSize: 12, mb: 0.5, color: theme.palette.text.secondary }}
                          >
                            <strong>Geplant:</strong> {afterSchedule ? formatScheduledDateBerlin(afterSchedule) : '—'}
                          </Typography>
                        )}
                        <SanitizedHtmlBox
                          html={h.mail_content_snapshot}
                          sx={{ fontSize: 12.5, lineHeight: 1.55, '& p': { m: 0, mb: 0.5 } }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      data-testid={`diff-after-${entry.id}-${idx}`}
                      sx={{
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        p: 1,
                      }}
                    >
                      <Typography sx={{ ...UPPER_LABEL_SX, fontSize: 10, color: theme.palette.text.disabled, mb: 0.5 }}>
                        Erste Fassung — kein Vorher vorhanden
                      </Typography>
                      <Typography sx={{ fontSize: 12, mb: 0.5, color: theme.palette.text.secondary }}>
                        <strong>Betreff:</strong> {afterSubject || '—'}
                      </Typography>
                      {afterSchedule && (
                        <Typography
                          data-testid={`schedule-after-${entry.id}-${idx}`}
                          sx={{ fontSize: 12, mb: 0.5, color: theme.palette.text.secondary }}
                        >
                          <strong>Geplant:</strong> {formatScheduledDateBerlin(afterSchedule)}
                        </Typography>
                      )}
                      <SanitizedHtmlBox
                        html={h.mail_content_snapshot}
                        sx={{ fontSize: 12.5, lineHeight: 1.55, '& p': { m: 0, mb: 0.5 } }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {(canEdit || canCancel || editPending) && (
        <Box
          sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          {editPending ? (
            // Locked: a saved edit is awaiting its refresh, so the row still shows the PRE-edit
            // content/date. Replace the edit/cancel buttons with a non-interactive "wird
            // aktualisiert" indicator until the refreshed version lands, so the operator can't
            // re-open the dialog on stale data (Audit/Review P2).
            <Box
              data-testid={`edit-pending-${entry.id}`}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: theme.palette.text.secondary,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <CircularProgress size={16} color="inherit" />
              Wird aktualisiert…
            </Box>
          ) : (
            <>
              {canEdit && (
                <Button
                  data-testid={`edit-button-${entry.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(entry);
                  }}
                  startIcon={<EditOutlinedIcon />}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                  }}
                >
                  Bearbeiten
                </Button>
              )}
              {canCancel && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(entry);
                  }}
                  startIcon={<CancelOutlinedIcon />}
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#EF4444',
                    border: `1px solid ${alpha('#EF4444', 0.4)}`,
                    bgcolor: alpha('#EF4444', 0.08),
                    '&:hover': { bgcolor: alpha('#EF4444', 0.16) },
                  }}
                >
                  Versand abbrechen
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {/* Resend action for already-sent rows (RITA-1316): re-send the last mail
          without a reason. The mail goes out again and a new "Erneut versendet"
          history entry appears; the parent refetches the log on success. */}
      {canResend && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {resendError && (
            <Alert severity="error" sx={{ width: '100%', fontSize: 13 }}>
              {resendError}
            </Alert>
          )}
          {/* Three mutually exclusive states (avoids a nested ternary):
              1. delivered-but-unrecorded (commit-phase failure) → terminal warning, no button
              2. normal confirmed resend → success note until the durable refresh lands
              3. otherwise → the resend button */}
          {resendDeliveredUnrecorded && (
            <Alert
              severity="warning"
              data-testid={`resend-unrecorded-${entry.id}`}
              sx={{ width: '100%', fontSize: 13 }}
            >
              Die E-Mail wurde erneut versendet, konnte aber nicht vollständig protokolliert werden. Bitte lade die
              Ansicht neu und versende sie nicht erneut.
            </Alert>
          )}
          {!resendDeliveredUnrecorded && resendConfirmed && (
            <Alert severity="success" data-testid={`resend-success-${entry.id}`} sx={{ width: '100%', fontSize: 13 }}>
              E-Mail wurde erneut versendet.
            </Alert>
          )}
          {!resendDeliveredUnrecorded && !resendConfirmed && (
            <Button
              data-testid={`resend-button-${entry.id}`}
              onClick={(e) => {
                e.stopPropagation();
                handleResend();
              }}
              disabled={resending}
              startIcon={resending ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: RESEND_COLOR,
                border: `1px solid ${alpha(RESEND_COLOR, 0.4)}`,
                bgcolor: alpha(RESEND_COLOR, 0.08),
                '&:hover': { bgcolor: alpha(RESEND_COLOR, 0.16) },
                '&.Mui-disabled': { color: alpha(RESEND_COLOR, 0.5), borderColor: alpha(RESEND_COLOR, 0.2) },
              }}
            >
              {resending ? 'Wird versendet…' : 'Erneut versenden'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export default SystemAccessLogDetail;
